// Hospital.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import { Avatar } from "@progress/kendo-react-layout";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import { FiEdit } from "react-icons/fi";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import {
  activeInactiveHospital,
  deleteHospital,
  getAllHospitals,
} from "./services/hospital.service";
import {
  clearFilterHospital,
  clearHospitalID,
  setFilterHospital,
  setHospitalID,
} from "./hospitalSlice";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";

const Hospital: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    // dispatch(getAllHospitals());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const handleCreate = () => {
    navigate("/hospital/create");
  };

  return (
    <>
      <DeleteHospitalDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Hospital List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New Hospital
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <HospitalGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const HospitalGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const HospitalList = useAppSelector((state) => state.hospital.HospitalList);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const loading = useAppSelector((state) => state.hospital.loading);
  const FilterHospital = useAppSelector(
    (state) => state.hospital.FilterHospital,
  );

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  useEffect(() => {
    const payload = {
      ...FilterHospital,
      Limit: +pageLimit,
      Skip: +currentPage * +pageLimit,
    };
    dispatch(getAllHospitals(payload));
  }, [currentPage, pageLimit]);

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const MyEditCommandCell = (props: any) => {
    const handleEdit = (ID: number) => {
      navigate("/hospital/edit", { state: { HospitalID: ID } });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteHospital"));
      dispatch(setHospitalID(ID));
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.tdProps.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const MyActiveInActiveCommandCell = (props: any) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false,
    );

    const handleActiveInActive = async () => {
      setIsActive(!IsActive);
      const data = { ID: props.dataItem.ID, IsActive: !IsActive };
      const res = await dispatch(activeInactiveHospital(data));
      if (res.meta.requestStatus === "fulfilled") {
        dispatch(
          getAllHospitals({
            ...FilterHospital,
            Limit: +pageLimit,
            Skip: +currentPage * +pageLimit,
          }),
        );
      }
    };

    return (
      <td
        {...props.tdProps}
        style={{ textAlign: "center", ...props.tdProps.style }}
        className={props.className}
      >
        <Switch
          disabled={!isEditAccess}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  const HospitalImageCell = (props: GridCellProps) => (
    <td style={{ ...props.style }} className={props.className}>
      <Avatar
        style={{ width: 50, height: 50, flexBasis: 50 }}
        rounded={"full"}
        type={"image"}
      >
        {props.dataItem?.MainImage ? (
          <img
            src={props.dataItem?.MainImage || "/placeholder-hospital.png"}
            alt={props.dataItem?.HospitalName}
            style={{
              objectFit: "cover",
              width: 50,
              height: 50,
              cursor: "pointer",
            }}
            onClick={() => window.open(props.dataItem?.MainImage, "_blank")}
          />
        ) : (
          <div
            style={{
              fontSize: "14px",
              color: "#fff",
              height: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {"H"}
          </div>
        )}
      </Avatar>
    </td>
  );

  const EmergencyCell = (props: GridCellProps) => (
    <td style={{ ...props.style }} className={props.className}>
      <span
        style={{
          padding: "4px 8px",
          borderRadius: "4px",
          backgroundColor: props.dataItem?.IsEmergencyAvailable
            ? "#d4edda"
            : "#f8d7da",
          color: props.dataItem?.IsEmergencyAvailable ? "#155724" : "#721c24",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {props.dataItem?.IsEmergencyAvailable ? "YES" : "NO"}
      </span>
    </td>
  );

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 8 || props.index === 9 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => (
    <td {...props.tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const HospitalFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        numberfiels={["TotalBed", "TotalDoctors"]}
        filterSelector={(state) => state.hospital.FilterHospital}
        setFilterAction={setFilterHospital}
        clearFilterAction={clearFilterHospital}
        fetchDataAction={getAllHospitals}
        pagination={{ case: "capitalize" }}
      />
    ),
    [],
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 200px)" }}
        filterable={true}
        filter={filter}
        className="responsive-table"
        data={HospitalList}
        onFilterChange={handleFilterChange}
        skip={currentPage * pageLimit}
        take={pageLimit}
        total={HospitalList[0]?.ItemCount || HospitalList.length}
        pageable={{
          buttonCount: 5,
          pageSizes: [5, 10, 15, 20, 25, 50],
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="MainImage"
          title="Image"
          width={"80px"}
          cells={{ data: HospitalImageCell }}
          filterable={false}
        />

        <Column
          field="HospitalName"
          title="Hospital Name"
          width={"250px"}
          cells={{
            data: (props: any) => (
              <td>
                {isEditAccess ? (
                  <div
                    className="hoverableName"
                    onClick={() =>
                      navigate("/hospital/edit", {
                        state: { HospitalID: props.dataItem?.ID },
                      })
                    }
                  >
                    {props.dataItem?.HospitalName}
                  </div>
                ) : (
                  <div className="normalName">
                    {props.dataItem?.HospitalName}
                  </div>
                )}
              </td>
            ),
            filterCell: HospitalFilterCell,
          }}
        />
        <Column
          field="Area"
          title="Area"
          width={"150px"}
          cells={{
            filterCell: HospitalFilterCell,
          }}
        />
        <Column
          field="CityName"
          title="City"
          width={"150px"}
          cells={{
            filterCell: HospitalFilterCell,
          }}
        />
        <Column
          field="StateName"
          title="State"
          width={"130px"}
          cells={{
            filterCell: HospitalFilterCell,
          }}
        />
        <Column
          field="TotalBed"
          title="Beds"
          width={"100px"}
          cells={{
            filterCell: HospitalFilterCell,
          }}
        />
        <Column
          field="TotalDoctors"
          title="Doctors"
          width={"110px"}
          cells={{
            filterCell: HospitalFilterCell,
          }}
        />
        <Column
          field="IsEmergencyAvailable"
          title="Emergency"
          width={"110px"}
          cells={{ data: EmergencyCell }}
          filterable={false}
        />
        {/* <Column
          field="Ratings"
          title="Rating"
          width={"120px"}
          filterable={false}
        /> */}
        <Column
          field="IsActive"
          title="Status"
          width={"90px"}
          cells={{ data: MyActiveInActiveCommandCell }}
          filterable={false}
          locked={true}
        />
        {(isEditAccess || isDeleteAccess) && (
          <Column
            field="ID"
            title="Actions"
            width={"130px"}
            cells={{ data: MyEditCommandCell }}
            filterable={false}
            locked={true}
          />
        )}
      </KendoGrid>
      {!HospitalList?.length && !loading && <AlertBox />}
    </>
  );
};

const DeleteHospitalDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const HospitalID = useAppSelector((state) => state.hospital.HospitalID);
  const loading = useAppSelector((state) => state.hospital.loading);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterHospital = useAppSelector(
    (state) => state.hospital.FilterHospital,
  );

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteHospital(ID));
      if (response.payload?.Data?.Status === 200) {
        dispatch(
          getAllHospitals({
            ...FilterHospital,
            Limit: +pageLimit,
            Skip: +currentPage * +pageLimit,
          }),
        );
        dispatch(closeDialog());
        dispatch(clearHospitalID());
      } else {
        dispatch(
          getAllHospitals({
            ...FilterHospital,
            Limit: +pageLimit,
            Skip: +currentPage * +pageLimit,
          }),
        );
        dispatch(closeDialog());
        dispatch(clearHospitalID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteHospital" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Hospital</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this hospital?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{ display: "flex", justifyContent: "end", gap: "10px" }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(HospitalID)}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Hospital;
